import { lazy } from 'react';
import LogIn from '../Authentication/components/LogIn';
import LogOut from '../Authentication/components/LogOut';
import Unauthorized from './views/Unauthorized';
import Dashboard from "../Dashboard";
import ManageApplications from "../ManageApplications/components";
import Reviewers from "../Reviewers/components";
import ManageReviewer from "../ManageReviewer/components";
import Search from "../Search/components";
import { AUTH_ROLES, REPORTS_LABELS } from "../util/constants";

const Applicant = lazy(() => import('../Applicant/components'));
const ManageHSCycles = lazy(() => import('../ManageHSCycles/components'));
const ReportsDash = lazy(() => import('../ReportsDash/components'));
const Report = lazy(() => import('../Report'));
const ConfigurationsMain = lazy(() => import('../Configurations'));

const StudentDashboard = lazy(() => import('../StudentDashboard'));

// Scholars-only surfaces (no Honors counterpart) — reached via the program-type switcher (DD-1).
const ScholarsCommonQueue = lazy(() => import('../ManageApplications/components/ScholarsCommonQueue'));
const ScholarsReviewCascade = lazy(() => import('../ManageApplications/components/ScholarsReviewCascade'));
const ScholarsAlternateList = lazy(() => import('../ManageApplications/components/ScholarsAlternateList'));
const ScholarsProgramConfig = lazy(() => import('../Configurations/ScholarsProgramConfig'));
const ScholarsPdnyConfig = lazy(() => import('../Configurations/ScholarsPdnyConfig'));
const ScholarsRatingsConfig = lazy(() => import('../Configurations/ScholarsRatingsConfig'));
const ScholarsReviewers = lazy(() => import('../ManageReviewer/components/ScholarsReviewers'));
const ScholarsReportsDash = lazy(() => import('../ScholarsReportsDash/components'));
const ScholarsApplicant = lazy(() => import('../Applicant/components/ScholarsApplicant'));
const ScholarsApplicantHistory = lazy(() => import('../ManageApplications/components/ScholarsApplicantHistory'));

const PATHS = [
    {
        path: "/",
        title: "Dashboard",
        Component: Dashboard, 
        roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.HONORS_REVIEWER}, ${AUTH_ROLES.SCHOLARS_REVIEWER}, ${AUTH_ROLES.STUDENT_EMPLOYEE}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}, ${AUTH_ROLES.HONORS_PROGRAM_COORDINATOR}`,
    },
        { // Dashboard subpages
            title: "My Application",
            path: "/ApplicationStatus/:applicantEmployeeId",
            Component: StudentDashboard,
        },
        {
            path: "/applicant/:applicantEmployeeId/:programId/:term",
            title: "Applicant Details", 
            Component: Applicant, 
            roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.HONORS_REVIEWER}, ${AUTH_ROLES.HONORS_PROGRAM_COORDINATOR}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER}, ${AUTH_ROLES.SCHOLARS_REVIEWER}`,
        },
    {
        path: "/manage-applications",
        title: "Manage Applications",
        Component: ManageApplications,
    },
    {
        title: "Reports",
        path: "/reports",
        Component: ReportsDash,
        roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}`
    },
        { // Report subpages
            title: "Report - In Review",
            path: `/reports/${REPORTS_LABELS["inreview"].KEY}/:programId?`,
            Component: Report,
            roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}`,
        },
        { // Report subpages
            title: "Report - Past Due",
            path: `/reports/${REPORTS_LABELS["pastdue"].KEY}/:programId?`,
            Component: Report,
            roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}`,
        },
        { // Report subpages
            title: "Report - Not Started",
            path: `/reports/${REPORTS_LABELS["notstarted"].KEY}/:programId?`,
            Component: Report,
            roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}`,
        },
        { // Report subpages
            title: getReportTitle,
            path: "/reports/:reportId/:programId?",
            Component: Report,
            roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}`,
        },
    {
        path: "/reviewers/:active?/:bulk?",
        title: "Reviewers",
        Component: Reviewers,
        roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
        { // Manage Reviewers subpages
            title: "Manage Reviewer",
            path: "/reviewer/:reviewerEmployeeId?",
            Component: ManageReviewer,
            roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}`,
        },
    {
        path: "/configurations/:programId?",
        title: "Configurations",
        Component: ConfigurationsMain, 
        roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Honors cycle configuration (CFG-FR-51). "cycle" is a reserved programId — React Router v6
      // ranks static segments above dynamic, so this outranks /configurations/:programId? regardless
      // of declaration order. HONORS_ADMIN deliberately absent — no GraphQL operation authorizes it (Q2=D).
        title: "Cycle Configuration",
        path: "/configurations/cycle",
        Component: ManageHSCycles,
        roles: `${AUTH_ROLES.ADMIN_ACCESS}`,
    },
    {
        path: "/search/:param?",
        title: "Search",
        Component: Search,
        roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.HONORS_ADMIN}, ${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.STUDENT_EMPLOYEE}, ${AUTH_ROLES.HONORS_SEASONAL_MANAGER}, ${AUTH_ROLES.HONORS_PROGRAM_COORDINATOR}`,
    },
    {
        path: "/scholars/search/:param?",
        title: "Search",
        Component: Search,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars common queue + pull-next (E2/E4/F3)
        title: "Scholars Queue",
        path: "/scholars/queue/:subProgramId?",
        Component: ScholarsCommonQueue,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER}, ${AUTH_ROLES.SCHOLARS_REVIEWER}`,
    },
    { // Scholars Review Cascade — admin overview of where applicants sit in the ranked-preference cascade,
      // with a "move to next queue" action. ADMIN-only (mirrors the decisioning surface it drives).
        title: "Review Cascade",
        path: "/scholars/cascade",
        Component: ScholarsReviewCascade,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // has507 — Scholars-specific applicant details page (separate from shared Honors version)
        title: "Scholars Applicant Details",
        path: "/scholars/applicant/:applicantEmployeeId/:term",
        Component: ScholarsApplicant,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER}, ${AUTH_ROLES.SCHOLARS_REVIEWER}`,
    },
    { // Admin-only applicant history — full audit-trail timeline for any applicant id, backed by
      // ScholarsAuditEvent rows. Exposes actor IDs + raw before/after state, so ADMIN-only
      // (SCHOLARS-ADMIN, plus the Honors-side ADMIN-ACCESS). Server resolver is the authoritative gate.
        title: "Applicant History",
        path: "/scholars/applicant-history",
        Component: ScholarsApplicantHistory,
        roles: `${AUTH_ROLES.ADMIN_ACCESS}, ${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars opt-in list (G7) — read-only view; PMs admit/deny from the applicant detail page.
        title: "Scholars Opt-In List",
        path: "/scholars/opt-in",
        Component: ScholarsAlternateList,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER}`,
    },
    { // Scholars cycle configuration (CFG-FR-51). "cycle" is a reserved programId — same ranked-matching
      // mechanism as the Honors route above. HONORS_ADMIN deliberately absent (Q2=D).
        title: "Scholars Cycle Configuration",
        path: "/scholars/configurations/cycle",
        Component: ManageHSCycles,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars program configuration (B1–B4, A2)
        title: "Scholars Configuration",
        path: "/scholars/configurations",
        Component: ScholarsProgramConfig,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars Presumptive Deny criteria management (B4, A2)
        title: "Scholars PDNY Configuration",
        path: "/scholars/configurations/pdny",
        Component: ScholarsPdnyConfig,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars Ratings configuration — standalone page mirroring the Honors ratings config (Option B).
      // "ratings" is a reserved programId segment; React Router v6 ranks it above /scholars/configurations.
        title: "Scholars Ratings Configuration",
        path: "/scholars/configurations/ratings",
        Component: ScholarsRatingsConfig,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars reviewer management (F1/F2)
        title: "Scholars Reviewers",
        path: "/scholars/reviewers",
        Component: ScholarsReviewers,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}`,
    },
    { // Scholars reports dashboard (front-end port of applicable Honors reports)
        title: "Scholars Reports",
        path: "/scholars/reports",
        Component: ScholarsReportsDash,
        roles: `${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER}`,
    },
        { // Scholars report subpages — reuse the shared Report datagrid, scoped by the /scholars path.
          // Authoritative per-report authz is server-side (future backend card).
            title: getReportTitle,
            path: "/scholars/reports/:reportId/:programId?",
            Component: Report,
            roles: `${AUTH_ROLES.SCHOLARS_ADMIN}, ${AUTH_ROLES.SCHOLARS_PROGRAM_MANAGER}`,
        },
];

// Scholars reuses the shared report keys but renames a few for its process. Only the differing
// labels are listed; everything else falls back to the shared REPORTS_LABELS (Honors) label.
const SCHOLARS_REPORT_TITLE_OVERRIDES = {
    notstarted: "Not Preferenced",
    pendingpreferences: "Pending Preferences",
    preferencesreceived: "Preferences Received",
};

function getReportTitle({ location, params }) {
    const isScholars = (location?.pathname ?? "").startsWith("/scholars");
    const sharedLabel = REPORTS_LABELS[params?.reportId]?.LABEL ?? "Unknown report";
    const reportLabel = isScholars
        ? (SCHOLARS_REPORT_TITLE_OVERRIDES[params?.reportId] ?? sharedLabel)
        : sharedLabel;
    return `Report - ${reportLabel}`;
}

export const authorizationRoutes = {
    login: "/login",
    logout: "/logout",
    unauthorized: "/unauthorized",
  };

const authenticationFlowPaths = [
    {
        path: authorizationRoutes.login,
        title: "Log In",
        Component: LogIn 
    },
    {
        path: authorizationRoutes.logout,
        title: "Log Out",
        Component: LogOut 
    },
    {
        path: authorizationRoutes.unauthorized,
        title: "Not authorized",
        Component: Unauthorized 
    }
]

export {
    authenticationFlowPaths,
    PATHS as default
}